import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const CommentSection = ({ postId }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState({ name: '', email: '', content: '' });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  useEffect(() => {
    const fetchComments = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/posts/${postId}/comments`);
        setComments(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching comments:', err);
        setError('Failed to load comments. Please try again later.');
        setLoading(false);
      }
    };
    
    if (postId) {
      fetchComments();
    }
  }, [postId]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewComment(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    // Validate inputs
    if (!newComment.name || !newComment.email || !newComment.content) {
      setError('All fields are required');
      setSubmitting(false);
      return;
    }
    
    try {
      const response = await axios.post(`/api/posts/${postId}/comments`, newComment);
      
      // Add the new comment to the list
      setComments(prev => [response.data, ...prev]);
      setNewComment({ name: '', email: '', content: '' });
      setSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error submitting comment:', err);
      setError('Failed to submit comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <section className="comments-section">
      <h2 className="comments-title">Comments ({comments.length})</h2>
      
      {/* Comment Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h3 className="text-xl font-semibold mb-4">Leave a Comment</h3>
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
            <p className="text-green-700">Your comment has been submitted successfully!</p>
          </div>
        )}
        
        <form onSubmit={handleSubmitComment} className="comment-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name" className="form-label">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={newComment.name}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Your name"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={newComment.email}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Your email (will not be published)"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="content" className="form-label">Comment</label>
            <textarea
              id="content"
              name="content"
              value={newComment.content}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder="Your comment"
              rows="4"
            ></textarea>
          </div>
          
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Post Comment'}
          </button>
        </form>
      </div>
      
      {/* Comments List */}
      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="spinner"></div>
        </div>
      ) : comments.length > 0 ? (
        <div className="space-y-6">
          {comments.map(comment => (
            <div key={comment._id} className="comment">
              <div className="comment-avatar">
                {comment.name.charAt(0)}
              </div>
              <div className="comment-content">
                <div className="comment-header">
                  <span className="comment-author">{comment.name}</span>
                  <span className="comment-date">
                    {new Date(comment.createdAt).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </span>
                </div>
                <div className="comment-body">
                  <p>{comment.content}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500">No comments yet. Be the first to comment!</p>
        </div>
      )}
    </section>
  );
};

export default CommentSection;
